import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useFormatCurrency } from "@/context/CountryContext";
import { TrendingDown, Flame } from "lucide-react";

const CATEGORY_EMOJI = {
  "Alimentaire": "🛒",
  "Épices & Condiments": "🌶️",
  "Boissons": "🥤",
  "Fruits & Légumes": "🥦",
  "Produits Laitiers": "🥛",
  "Boulangerie": "🍞",
  "Hygiène": "🧴",
  "Électronique": "📱",
  "Vêtements": "👗",
  "Pharmacie": "💊",
};

function getCatEmoji(category) {
  if (!category) return "🏷️";
  for (const [k, v] of Object.entries(CATEGORY_EMOJI)) {
    if (category.toLowerCase().includes(k.toLowerCase())) return v;
  }
  return "🏷️";
}

export default function PriceTicker() {
  const [items, setItems] = useState([]);
  const [paused, setPaused] = useState(false);
  const fmt = useFormatCurrency();
  const trackRef = useRef(null);

  useEffect(() => {
    api.get("/top-prices").then(r => setItems(r.data)).catch(() => {});
  }, []);

  if (items.length === 0) return null;

  // Duplicate for seamless loop
  const display = [...items, ...items];

  return (
    <div
      className="relative overflow-hidden bg-gradient-to-r from-primary/90 via-primary to-primary/90 border-b border-primary/20"
      style={{ height: "36px" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Label gauche */}
      <div className="absolute left-0 top-0 bottom-0 z-10 flex items-center gap-1.5 px-3 bg-gradient-to-r from-orange-600 to-primary text-white text-xs font-bold shrink-0 select-none">
        <Flame className="w-3.5 h-3.5 animate-pulse" />
        <span className="hidden sm:inline">TOP PRIX</span>
        <TrendingDown className="w-3.5 h-3.5" />
      </div>

      {/* Fade edges */}
      <div className="absolute left-[72px] sm:left-[90px] top-0 bottom-0 w-8 z-10 bg-gradient-to-r from-primary to-transparent pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-8 z-10 bg-gradient-to-l from-primary to-transparent pointer-events-none" />

      {/* Track */}
      <div
        ref={trackRef}
        className="flex items-center h-full pl-[90px]"
        style={{
          animation: paused ? "none" : "ticker-scroll 40s linear infinite",
          whiteSpace: "nowrap",
        }}
      >
        {display.map((item, i) => (
          <Link
            key={`${item.product_id}-${i}`}
            to="/recherche"
            className="inline-flex items-center gap-2 px-5 text-white/95 hover:text-white transition-colors group shrink-0"
          >
            <span className="text-sm">{getCatEmoji(item.category)}</span>
            <span className="text-xs font-semibold group-hover:underline">{item.name}</span>
            <span className="text-xs font-bold bg-white/20 rounded px-1.5 py-0.5 tabular-nums">
              {fmt(item.price)}
              {item.unit ? <span className="font-normal opacity-80"> /{item.unit}</span> : ""}
            </span>
            <span className="text-[10px] opacity-60">{item.shop_name}</span>
            <span className="text-white/20 select-none mx-1">·</span>
          </Link>
        ))}
      </div>

      <style>{`
        @keyframes ticker-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
